'use client';

import { UploadSimple, Warning } from '@phosphor-icons/react/ssr';
import {
  type DragEvent,
  type ElementType,
  type KeyboardEvent,
  type ReactNode,
  useId,
  useRef,
  useState,
} from 'react';

import { tv } from '../../lib/tv';
import { Link } from './link';

/** Why a file was turned away before upload. */
export type FileRejectReason = 'type' | 'size' | 'count';
export type FileRejection = { file: File; reason: FileRejectReason };

const DEFAULT_MESSAGES: Record<FileRejectReason, string> = {
  type: 'That file type isn’t supported.',
  size: 'That file is too large.',
  count: 'Choose one file at a time.',
};

/** Does `file` match an `accept` list ("video/*,.mov,video/mp4")? */
export function acceptsFile(file: File, accept?: string): boolean {
  if (!accept?.trim()) return true;
  const name = file.name.toLowerCase();
  const type = file.type.toLowerCase();
  return accept.split(',').some((raw) => {
    const token = raw.trim().toLowerCase();
    if (!token) return false;
    if (token.startsWith('.')) return name.endsWith(token);
    if (token.endsWith('/*')) return type.startsWith(token.slice(0, -1));
    return type === token;
  });
}

/** Split files into accepted and rejected (type, then size, then count). */
export function validateFiles(
  files: File[],
  {
    accept,
    maxSize,
    multiple = true,
  }: { accept?: string; maxSize?: number; multiple?: boolean },
) {
  const accepted: File[] = [];
  const rejected: FileRejection[] = [];
  for (const file of files) {
    if (!acceptsFile(file, accept)) rejected.push({ file, reason: 'type' });
    else if (maxSize !== undefined && file.size > maxSize)
      rejected.push({ file, reason: 'size' });
    else if (!multiple && accepted.length) rejected.push({ file, reason: 'count' });
    else accepted.push(file);
  }
  return { accepted, rejected };
}

export type DropzoneProps = {
  /** File types, as for `<input accept>`: "video/mp4,video/quicktime,.mov". */
  accept?: string;
  /** Accept several files (default). Figma: accept multiple unless the flow needs one. */
  multiple?: boolean;
  /** Largest accepted file, in bytes. */
  maxSize?: number;
  /** Files that passed `accept` / `maxSize` / `multiple`. */
  onFiles?: (files: File[]) => void;
  /** Files turned away, with the reason. The first reason shows as the Error state. */
  onReject?: (rejections: FileRejection[]) => void;
  /** Title before the browse link. */
  title?: ReactNode;
  /** The browse "link" (part of the button, not a separate control). */
  browseLabel?: ReactNode;
  /** Formats, size and hints: "MP4 or MOV · up to 2 GB · 9:16 recommended". */
  caption?: ReactNode;
  /** Title while files are dragged over. */
  dropTitle?: ReactNode;
  /** Force the Error state with this reason (e.g. from the server); otherwise rejections set it. */
  error?: ReactNode;
  /** How to fix it - the caption in the Error state. Defaults to `caption`. */
  errorHint?: ReactNode;
  /** The browse link in the Error state. */
  retryLabel?: ReactNode;
  /** Error texts per rejection reason. */
  messages?: Partial<Record<FileRejectReason, string>>;
  disabled?: boolean;
  /** Why it is disabled - the title in the Disabled state. */
  disabledTitle?: ReactNode;
  /** The caption in the Disabled state, e.g. when to try again. */
  disabledHint?: ReactNode;
  /** Icon in the circle. */
  icon?: ElementType<{ className?: string }>;
  /** Name for the hidden file input (forms). */
  name?: string;
  id?: string;
  className?: string;
};

const dropzone = tv({
  slots: {
    root: 'relative flex w-full',
    zone: [
      'relative flex min-h-52 w-full cursor-pointer flex-col items-center justify-center gap-2.5 rounded-lg px-6 py-8 text-center outline-none',
      'transition-[background-color,box-shadow] duration-[120ms] ease-out',
      'focus-visible:shadow-focus-ring',
    ],
    // The dashed 1.5px inside stroke (Figma dash 6, gap 5): CSS dashes can't be sized.
    outline: 'pointer-events-none absolute inset-0 size-full overflow-visible',
    circle: 'flex size-12 shrink-0 items-center justify-center rounded-full',
    icon: 'size-[22px]',
    title:
      'text-body-md-strong text-text-primary flex flex-wrap items-baseline justify-center gap-1',
    caption: 'text-caption text-text-tertiary',
  },
  variants: {
    state: {
      default: {
        zone: 'bg-bg-subtle',
        outline: 'text-border-strong',
        circle: 'bg-brand-subtle',
        icon: 'text-icon-brand',
      },
      dragging: {
        zone: 'bg-brand-subtle inset-ring-2 inset-ring-brand-strong',
        outline: 'hidden',
        circle: 'bg-bg-surface',
        icon: 'text-icon-brand',
        // Figma text/tertiary on brand/subtle is 4.42:1 - one step darker passes.
        caption: 'text-text-secondary',
      },
      error: {
        zone: 'bg-status-danger-bg',
        outline: 'text-status-danger-fg',
        circle: 'bg-bg-surface',
        icon: 'text-status-danger-fg',
        title: 'text-status-danger-fg',
      },
      disabled: {
        zone: 'bg-bg-disabled cursor-not-allowed',
        outline: 'text-border-default',
        circle: 'bg-bg-muted',
        icon: 'text-icon-tertiary',
        title: 'text-text-disabled',
        caption: 'text-text-disabled',
      },
    },
  },
  defaultVariants: { state: 'default' },
});

/** A synthetic drag (tests) exposes names; a real one only counts items until the drop. */
function describeDrag(event: DragEvent) {
  const dt = event.dataTransfer;
  const names = Array.from(dt?.files ?? []).map((f) => f.name);
  const count =
    names.length || Array.from(dt?.items ?? []).filter((i) => i.kind === 'file').length;
  if (!count) return null;
  return `${count} ${count === 1 ? 'file' : 'files'}${names.length ? ` · ${names.join(', ')}` : ''}`;
}

/**
 * File drop target that is also a button (Figma: File Upload ↳ Dropzone):
 * drag files onto it, or click / Enter / Space to open the file picker (a
 * hidden native input). Validates `accept`, `maxSize` and `multiple` before
 * anything uploads; turned-away files show the Error state with the reason
 * and how to fix it (announced), accepted ones go to `onFiles`. Drag over =
 * solid 2px Deep Olive border, Pale Sage fill, "Drop to upload". Disabled
 * says why (`disabledTitle`).
 */
export function Dropzone({
  accept,
  multiple = true,
  maxSize,
  onFiles,
  onReject,
  title = 'Drag files here or',
  browseLabel = 'browse files',
  caption,
  dropTitle = 'Drop to upload',
  error: errorProp,
  errorHint,
  retryLabel = 'Choose another file',
  messages,
  disabled = false,
  disabledTitle = 'Uploads are unavailable',
  disabledHint,
  icon: Icon = UploadSimple,
  name,
  id,
  className,
}: DropzoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const depth = useRef(0);
  const [drag, setDrag] = useState<string | null>(null);
  const [rejection, setRejection] = useState<FileRejectReason | null>(null);
  const titleId = useId();
  const captionId = useId();

  const errorText =
    errorProp ??
    (rejection ? (messages?.[rejection] ?? DEFAULT_MESSAGES[rejection]) : null);
  const state = disabled
    ? 'disabled'
    : drag !== null
      ? 'dragging'
      : errorText
        ? 'error'
        : 'default';
  const styles = dropzone({ state });
  const StateIcon = state === 'error' ? Warning : Icon;

  const take = (list: FileList | null) => {
    const files = Array.from(list ?? []);
    if (!files.length) return;
    const { accepted, rejected } = validateFiles(files, { accept, maxSize, multiple });
    setRejection(rejected[0]?.reason ?? null);
    if (rejected.length) onReject?.(rejected);
    if (accepted.length) onFiles?.(accepted);
  };

  const browse = () => {
    if (!disabled) inputRef.current?.click();
  };

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      browse();
    }
  };

  const onDragEnter = (event: DragEvent) => {
    event.preventDefault();
    if (disabled) return;
    depth.current += 1;
    setDrag(describeDrag(event) ?? '');
  };
  const onDragOver = (event: DragEvent) => {
    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = disabled ? 'none' : 'copy';
  };
  const onDragLeave = () => {
    depth.current = Math.max(0, depth.current - 1);
    if (!depth.current) setDrag(null);
  };
  const onDrop = (event: DragEvent) => {
    event.preventDefault();
    depth.current = 0;
    setDrag(null);
    if (!disabled) take(event.dataTransfer?.files ?? null);
  };

  const heading =
    state === 'dragging' ? (
      dropTitle
    ) : state === 'disabled' ? (
      disabledTitle
    ) : (
      <>
        <span>{state === 'error' ? errorText : title}</span>
        <Link asChild>
          <span>{state === 'error' ? retryLabel : browseLabel}</span>
        </Link>
      </>
    );
  const hint =
    state === 'dragging'
      ? drag || caption
      : state === 'disabled'
        ? (disabledHint ?? caption)
        : state === 'error'
          ? (errorHint ?? caption)
          : caption;

  return (
    <div
      data-slot="dropzone"
      data-drop-state={state}
      className={styles.root({ className })}
    >
      <input
        ref={inputRef}
        id={id}
        name={name}
        type="file"
        accept={accept}
        multiple={multiple}
        disabled={disabled}
        tabIndex={-1}
        aria-hidden
        className="sr-only"
        onChange={(e) => {
          take(e.target.files);
          // Picking the same file again must fire change again.
          e.target.value = '';
        }}
      />
      <div
        role="button"
        tabIndex={0}
        aria-labelledby={titleId}
        aria-describedby={hint ? captionId : undefined}
        aria-disabled={disabled || undefined}
        data-slot="dropzone-target"
        className={styles.zone()}
        onClick={browse}
        onKeyDown={onKeyDown}
        onDragEnter={onDragEnter}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
      >
        <svg aria-hidden data-slot="dropzone-outline" className={styles.outline()}>
          <rect
            x="0.75"
            y="0.75"
            rx="11.25"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeDasharray="6 5"
            style={{ width: 'calc(100% - 1.5px)', height: 'calc(100% - 1.5px)' }}
          />
        </svg>
        <span data-slot="dropzone-circle" className={styles.circle()}>
          <StateIcon aria-hidden className={styles.icon()} />
        </span>
        <span
          id={titleId}
          data-slot="dropzone-title"
          data-disabled={disabled || undefined}
          className={styles.title()}
        >
          {heading}
        </span>
        {hint && (
          <span
            id={captionId}
            data-slot="dropzone-caption"
            data-disabled={disabled || undefined}
            className={styles.caption()}
          >
            {hint}
          </span>
        )}
      </div>
      <p role="status" className="sr-only">
        {state === 'error' ? errorText : null}
      </p>
    </div>
  );
}
